import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="express-screener"
//
// Powers the "Change household size" modal on the express charity-care screener. Picking a
// size updates the over/under question in place — the title threshold, both option labels,
// the household-size display, and the hidden household_size field — using a precomputed
// { size: threshold } map, so no page reload is needed. (The over/under answer posts a static
// "under"/"over" bracket, so the radio values themselves don't change with size.)
// Class sets that mirror the server-rendered selected / unselected states of the size buttons
// (see charity_care.html+express.erb). We toggle BOTH sets so a newly-selected button drops its
// `!bg-white` override (otherwise the important white background wins over the purple fill) and
// deselected buttons regain it — without this the highlight can't move off the initial size.
const SELECTED_CLASSES = ["bg-core-purple-500", "text-white", "border-core-purple-500"]
const UNSELECTED_CLASSES = ["!bg-white", "!text-support-dark-800", "border-support-light-300", "hover:!border-core-purple-400"]

export default class extends Controller {
  static targets = ["modal", "thresholdLabel", "sizeLabel", "householdSizeField", "numberButton"]
  static values = { thresholds: Object }

  // This controller only drives the known-household "Change" modal. The no-roster layout asks
  // household size on its own screen (plain radios + a GET submit), so it doesn't use this
  // controller — hence the target guards below for the modal-only elements.
  open() { if (this.hasModalTarget) this.modalTarget.classList.remove("hidden") }
  close() { if (this.hasModalTarget) this.modalTarget.classList.add("hidden") }

  select(event) {
    const size = parseInt(event.currentTarget.dataset.size, 10)
    const threshold = this.thresholdsValue[size]
    if (threshold === undefined) { this.close(); return }

    const formatted = this.currency(threshold)
    this.thresholdLabelTargets.forEach((el) => { this.updateThresholdLabel(el, formatted) })
    if (this.hasSizeLabelTarget) this.sizeLabelTarget.textContent = size
    if (this.hasHouseholdSizeFieldTarget) this.householdSizeFieldTarget.value = size
    // The selected over/under answer stays selected as-is (its value is a static "under"/"over"
    // bracket); only the displayed threshold labels change with the new size.

    this.numberButtonTargets.forEach((b) => {
      const selected = parseInt(b.dataset.size, 10) === size
      SELECTED_CLASSES.forEach((c) => b.classList.toggle(c, selected))
      UNSELECTED_CLASSES.forEach((c) => b.classList.toggle(c, !selected))
    })

    this.close()
  }

  currency(value) {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(value)
  }

  updateThresholdLabel(el, formatted) {
    const jackpotNumber = this.application.getControllerForElementAndIdentifier(el, "jackpot-number")
    if (jackpotNumber) {
      jackpotNumber.setValue(formatted)
    } else {
      el.textContent = formatted
    }
  }
}
